'use client';
import type { CSSProperties, ReactElement, ReactNode, RefObject } from 'react';
import { useMemo } from 'react';
import { ReactFlow, type ReactFlowInstance } from '@xyflow/react';
import { GraphArrowPanel, GraphControlsOverlay, GraphMiniMapPanel, GraphZoomPanel, GridPatternBackground, GRAPH_MINIMAP_HEIGHT, GRAPH_MINIMAP_WIDTH, type GraphNavDirection, } from '../Cmarkdowngraphcontrols';
import type { LayoutResult } from './layout-mindmap';
import { DEPTH_COLORS, CONTAINER_HEIGHT, FIT_VIEW_PADDING } from './mindmap-constants';
import { getMindmapMinimapNodeColor } from './mindmap-derived-model';
import { MindmapInteractionContext, type MindmapInteractionState, } from './mindmap-interaction-context';
import { MindmapEdge } from './MindmapEdge';
import { MindmapNode } from './MindmapNode';
import type { MindmapFlowEdge, MindmapFlowNode } from './mindmap-types';
import { dataEl, type DataElScope } from '@/utils/dataEl';
const FIT_VIEW_OPTIONS = {
    padding: FIT_VIEW_PADDING,
} as const;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 2;
const CSS_VALUE = {
    Transparent: 'transparent',
} as const;
const REACT_FLOW_WRAPPER_STYLE: CSSProperties = {
    position: 'relative',
    flex: '1 1 auto',
    minHeight: CONTAINER_HEIGHT,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
};
const REACT_FLOW_STYLE: CSSProperties = {
    width: '100%',
    flex: 1,
    minHeight: 0,
};
interface MindmapFlowCanvasProps {
    controlsScaleStyle: CSSProperties | undefined;
    dataElScope: DataElScope;
    interactionState: MindmapInteractionState;
    isZoomDisabled: boolean;
    layout: LayoutResult;
    onFlowInit: (instance: ReactFlowInstance<MindmapFlowNode, MindmapFlowEdge>) => void;
    onMoveZoomChange: (zoomLevel: number) => void;
    onNavigate: (direction: GraphNavDirection) => void;
    onNodeMouseEnter: (nodeId: string) => void;
    onNodeMouseLeave: () => void;
    onZoomChange: (targetZoom: number) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    reactFlowWrapperRef: RefObject<HTMLDivElement | null>;
    sideColumn: ReactNode;
    zoomLevel: number;
}
export function MindmapFlowCanvas({ controlsScaleStyle, dataElScope, interactionState, isZoomDisabled, layout, onFlowInit, onMoveZoomChange, onNavigate, onNodeMouseEnter, onNodeMouseLeave, onZoomChange, onZoomIn, onZoomOut, reactFlowWrapperRef, sideColumn, zoomLevel, }: MindmapFlowCanvasProps): ReactElement {
    // Built here rather than at module scope, and memoised so React Flow still
    // sees one stable identity for the life of the canvas. `MindmapNode`
    // renders markdown labels, which puts it on the markdown render cycle
    // (label -> Cmarkdown -> block renderer -> Cmarkdownmindmap -> this file);
    // reading it while the modules are still evaluating can hand back
    // `undefined`. The edge map is not on that cycle today, but it lives here
    // beside the node map so the pair cannot drift apart.
    const nodeTypes = useMemo(() => ({ mindmapCard: MindmapNode }), []);
    const edgeTypes = useMemo(() => ({ mindmapCurve: MindmapEdge }), []);
    return (<MindmapInteractionContext.Provider value={interactionState}>
        <div data-el={dataEl(dataElScope, 'MindmapFlowCanvas/Root')} ref={reactFlowWrapperRef} style={REACT_FLOW_WRAPPER_STYLE}>
        <ReactFlow<MindmapFlowNode, MindmapFlowEdge> nodes={layout.nodes} edges={layout.edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} style={REACT_FLOW_STYLE} fitView fitViewOptions={FIT_VIEW_OPTIONS} panOnDrag zoomOnScroll zoomOnPinch panOnScroll zoomOnDoubleClick={false} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} nodesDraggable={false} nodesConnectable={false} edgesReconnectable={false} elementsSelectable={false} nodesFocusable={false} edgesFocusable={false} disableKeyboardA11y preventScrolling onInit={onFlowInit} onMove={(_event, viewport) => {
            onMoveZoomChange(viewport.zoom);
        }} onNodeMouseEnter={(_event, node) => {
            onNodeMouseEnter(node.id);
        }} onNodeMouseLeave={onNodeMouseLeave} proOptions={{ hideAttribution: true }}>
          <GridPatternBackground />
          <GraphZoomPanel zoomLevel={zoomLevel} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM} disabled={isZoomDisabled} accentColor={DEPTH_COLORS[0]} scaleStyle={controlsScaleStyle} onZoomIn={onZoomIn} onZoomOut={onZoomOut} onZoomChange={onZoomChange}/>
          <GraphControlsOverlay scaleStyle={controlsScaleStyle} sideColumn={sideColumn}>
            <GraphArrowPanel onNavigate={onNavigate}/>
            <GraphMiniMapPanel width={GRAPH_MINIMAP_WIDTH} height={GRAPH_MINIMAP_HEIGHT} nodeColor={getMindmapMinimapNodeColor} nodeStrokeColor="rgba(255, 255, 255, 0.4)" nodeStrokeWidth={1.5} nodeBorderRadius={40} maskColor="rgba(10, 14, 22, 0.22)" bgColor={CSS_VALUE.Transparent} pannable zoomable/>
          </GraphControlsOverlay>
        </ReactFlow>
      </div>
    </MindmapInteractionContext.Provider>);
}
